import React from 'react';
import {createPortal} from 'react-dom';
import {Button} from '@mui/material';
import {BsList, BsMap} from 'react-icons/bs';
import {useVisualViewportBottomOffset} from '../../hooks/search/useVisualViewportBottomOffset';

interface SearchMapListToggleProps {
  isMapActive: boolean;
  showRequestLocationForm?: boolean;
  onToggle: () => void;
}

/**
 * Responsive toggle bar shown on small screens to switch between the map and list views.
 * Renders the two buttons (Map / List) with the active state highlighted.
 */
const SearchMapListToggle = ({isMapActive, onToggle, showRequestLocationForm}: SearchMapListToggleProps) => {
  // Continuously (not just while an input is focused) keeps the control
  // above whatever browser UI -- keyboard, address bar, or bottom toolbar --
  // is currently covering the screen. See issue #3106: a fixed CSS height
  // (100svh) that only updated during a keyboard session could leave the
  // control positioned behind Safari's toolbar once it reappeared on an
  // upward scroll after the keyboard had already closed.
  const bottomOffset = useVisualViewportBottomOffset();

  const controller = (
    <div
      className={showRequestLocationForm ? 'ss-display-none' : 'ss-small-screen-controller-wrapper'}
      style={{bottom: bottomOffset}}
    >
      <div className='ss-small-screen-controller'>
        <Button onClick={() => !isMapActive && onToggle()} variant='text' aria-label="Toggle map view" aria-pressed={isMapActive}>
          <span className={`ss-ctl-btn ${isMapActive ? "ss-ctl-btn-active" : ""} `}><BsMap/>Map</span>
        </Button>
        <Button onClick={() => isMapActive && onToggle()} variant='text' aria-label="Toggle list view" aria-pressed={!isMapActive}>
          <span className={`ss-ctl-btn ${!isMapActive ? "ss-ctl-btn-active" : ""} `}><BsList/>List</span>
        </Button>
      </div>
    </div>
  );

  // Keep the fixed control outside search-result layout changes. This also
  // prevents Safari from treating a changing ancestor as its containing block.
  return createPortal(controller, document.body);
};

export default SearchMapListToggle;
